import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  MenuItem,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import { useGetChequesQuery, useGetBankAccountsQuery } from '../../../store/api/financeApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function ChequesList() {
  const navigate = useNavigate();
  const [bankAccountId, setBankAccountId] = useState('');
  const [status, setStatus] = useState('');
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const { data: bankAccounts = [] } = useGetBankAccountsQuery();
  const { data, isLoading } = useGetChequesQuery({
    bankAccountId: bankAccountId || undefined,
    status: status || undefined,
    limit: 100,
  });
  const cheques = data?.cheques || [];

  const formatAmount = (v) => {
    const n = Number(v || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency , minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(n);
    } catch {
      return `${currency} ${n.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '–');

  const statusColor = (s) => {
    switch (s) {
      case 'cleared': return 'success';
      case 'bounced': return 'error';
      case 'cancelled': return 'default';
      default: return 'warning';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Cheques
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/finance/cheques/new')}>
          Add Cheque
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          size="small"
          label="Bank account"
          value={bankAccountId}
          onChange={(e) => setBankAccountId(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          {bankAccounts.map((b) => (
            <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="issued">Issued</MenuItem>
          <MenuItem value="cleared">Cleared</MenuItem>
          <MenuItem value="bounced">Bounced</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </TextField>
      </Box>

      <Card>
        <CardContent>
          {isLoading ? (
            <Typography color="text.secondary">Loading...</Typography>
          ) : cheques.length === 0 ? (
            <Typography color="text.secondary">No cheques found.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Cheque No</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Bank / Account</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Payee</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cheques.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.chequeNumber}</TableCell>
                      <TableCell>{row.bankAccount?.name || '–'} {row.bankAccount?.accountNumber ? `(${row.bankAccount.accountNumber})` : ''}</TableCell>
                      <TableCell>{formatDate(row.chequeDate)}</TableCell>
                      <TableCell>{row.payee || '–'}</TableCell>
                      <TableCell align="right">{formatAmount(row.amount)}</TableCell>
                      <TableCell>
                        <Chip label={row.status} color={statusColor(row.status)} size="small" />
                      </TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => navigate(`/finance/cheques/${row.id}`)}>
                          View / Edit
                        </Button>
                        <Button size="small" startIcon={<PrintIcon />} onClick={() => navigate(`/finance/cheques/${row.id}/print`)}>
                          Print
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default ChequesList;
